import { supabase } from '@/lib/supabase';
import type {
  ExecutionChatAction,
  ExecutionChatHitlRequestCas,
  ExecutionChatResponse,
} from '@shared/types/execution-chat';

const executionChatWorkerUrl = import.meta.env.VITE_EXECUTION_CHAT_WORKER_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isExecutionChatResponse = (value: unknown): value is ExecutionChatResponse =>
  isRecord(value) &&
  typeof value.executionId === 'string' &&
  typeof value.mode === 'string' &&
  (
    value.hitlRequestCas === null
    || (
      isRecord(value.hitlRequestCas)
      && Number.isInteger(value.hitlRequestCas.requestVersion)
      && (value.hitlRequestCas.requestVersion as number) > 0
      && typeof value.hitlRequestCas.previewHash === 'string'
      && /^[0-9a-f]{64}$/.test(value.hitlRequestCas.previewHash)
    )
  ) &&
  Array.isArray(value.messages);

const getExecutionChatWorkerUrl = (): string => {
  if (!executionChatWorkerUrl) {
    throw new Error(
      'Execution chat worker non configuré: définissez VITE_EXECUTION_CHAT_WORKER_URL.'
    );
  }

  return executionChatWorkerUrl;
};

const getAccessToken = async (): Promise<string> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('Session Supabase manquante pour le chat d’exécution.');
  }

  return accessToken;
};

const parseSseEvent = (rawEvent: string): { event: string; data: unknown } | null => {
  const lines = rawEvent
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return null;

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) return null;

  try {
    return { event, data: JSON.parse(dataLines.join('\n')) as unknown };
  } catch {
    return null;
  }
};

/**
 * No SSE event for this long ⇒ treat the stream as stalled and finalize via a refresh.
 * Must exceed the server's AI stall detector (180s by default): a reasoning model can
 * stay silent that long while still working, and the client must not give up first.
 */
const STREAM_INACTIVITY_TIMEOUT_MS = 190_000;

/**
 * Thrown when streaming fails. `established` distinguishes a failure before the
 * server accepted the request (safe to re-send) from one after the stream was
 * open (the message is likely already persisted, so re-sending would duplicate
 * it — the caller should refresh instead).
 */
export class ExecutionChatStreamError extends Error {
  readonly established: boolean;
  constructor(message: string, established: boolean) {
    super(message);
    this.name = 'ExecutionChatStreamError';
    this.established = established;
  }
}

const toStreamingError = async (response: Response): Promise<Error> => {
  const bodyText = await response.text();
  try {
    const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
    const message = parsed.message ?? parsed.error;
    if (typeof message === 'string' && message.length > 0) return new Error(message);
  } catch {
    // Ignore JSON parse failures.
  }
  return new Error(bodyText || 'Le streaming du chat d’exécution a échoué.');
};

export const invokeExecutionChat = async (
  input: ExecutionChatAction,
): Promise<ExecutionChatResponse> => {
  const accessToken = await getAccessToken();
  const response = await fetch(getExecutionChatWorkerUrl(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      apikey: supabaseAnonKey,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await toStreamingError(response);
  }

  const payload = (await response.json()) as unknown;
  if (!isExecutionChatResponse(payload)) {
    throw new Error('Réponse invalide du chat d’exécution.');
  }

  return payload;
};

/**
 * Send a message to execution-chat over SSE. Conversational replies stream
 * token-by-token via `onChunk`; the final reconciled context (including the
 * persisted assistant message) arrives in the returned response.
 */
export const streamExecutionChat = async (
  params: {
    executionId: string;
    message: string;
    hitlRequestCas?: ExecutionChatHitlRequestCas;
  },
  handlers: {
    onChunk?: (chunkText: string) => void;
    onStatus?: (statusText: string) => void;
  } = {},
): Promise<ExecutionChatResponse> => {
  const accessToken = await getAccessToken();

  const abortController = new AbortController();
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  const armInactivityTimer = (): void => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => abortController.abort(), STREAM_INACTIVITY_TIMEOUT_MS);
  };

  let response: Response;
  try {
    response = await fetch(getExecutionChatWorkerUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
        apikey: supabaseAnonKey,
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        action: 'send',
        executionId: params.executionId,
        message: params.message,
        ...(params.hitlRequestCas ? { hitlRequestCas: params.hitlRequestCas } : {}),
      }),
      signal: abortController.signal,
    });
  } catch (fetchError) {
    // Never reached the server (network/abort before response) — safe to retry.
    throw new ExecutionChatStreamError(
      fetchError instanceof Error ? fetchError.message : 'Connexion au chat impossible.',
      false,
    );
  }

  if (!response.ok) {
    const baseError = await toStreamingError(response);
    throw new ExecutionChatStreamError(baseError.message, false);
  }
  if (!response.body) {
    throw new ExecutionChatStreamError('Réponse de streaming invalide du chat d’exécution.', false);
  }

  // From here the request was accepted: a later failure must NOT re-send.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completedResponse: ExecutionChatResponse | null = null;
  armInactivityTimer();

  try {
    while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    armInactivityTimer();

    buffer += decoder.decode(value, { stream: true });

    while (true) {
      const separatorIndex = buffer.indexOf('\n\n');
      if (separatorIndex === -1) break;

      const rawEvent = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const parsedEvent = parseSseEvent(rawEvent);
      if (!parsedEvent) continue;

      if (parsedEvent.event === 'assistant_message_chunk') {
        const data = parsedEvent.data as { text?: unknown };
        if (typeof data.text === 'string' && data.text.length > 0) {
          handlers.onChunk?.(data.text);
        }
        continue;
      }

      if (parsedEvent.event === 'assistant_message_status') {
        const data = parsedEvent.data as { text?: unknown };
        if (typeof data.text === 'string' && data.text.length > 0) {
          handlers.onStatus?.(data.text);
        }
        continue;
      }

      if (parsedEvent.event === 'assistant_message_complete') {
        if (!isExecutionChatResponse(parsedEvent.data)) {
          throw new ExecutionChatStreamError('Réponse finale invalide du chat d’exécution.', true);
        }
        completedResponse = parsedEvent.data;
        continue;
      }

      if (parsedEvent.event === 'error') {
        const data = parsedEvent.data as { message?: unknown };
        throw new ExecutionChatStreamError(
          typeof data.message === 'string' ? data.message : 'Le streaming du chat a échoué.',
          true,
        );
      }
    }
  }
  } catch (streamErr) {
    if (streamErr instanceof ExecutionChatStreamError) throw streamErr;
    // Inactivity abort or a mid-stream read error: the server already accepted
    // the message, so signal "established" — the caller refreshes instead of
    // re-sending (which would duplicate the reply).
    throw new ExecutionChatStreamError(
      streamErr instanceof Error ? streamErr.message : 'Le streaming du chat a été interrompu.',
      true,
    );
  } finally {
    if (inactivityTimer) clearTimeout(inactivityTimer);
  }

  if (!completedResponse) {
    throw new ExecutionChatStreamError('Le streaming du chat s’est terminé sans réponse finale.', true);
  }

  return completedResponse;
};
